import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { AuthStore } from '@app/core/auth/auth.store';
import { AuthService, CurrentUserService, LocaleService } from '@app/core/services';
import { ThemeService, UiColor } from '@app/core/services/theme.service';
import { UserApi } from '@app/data/api';
import { NotificationService } from '@app/services/notification.service';
import { AppIcon } from '@app/shared/controls/app-icon/app-icon';
import { DropdownComponent } from '@app/shared/controls/app-dropdown/app-dropdown';
import { ProfileSettings } from '@app/shared/modals/profile-settings/profile-settings';

interface MenuOption<T> {
  readonly label: string;
  readonly value: T;
}

@Component({
  selector: 'app-profile-menu',
  imports: [AppIcon, DropdownComponent, FormsModule, ProfileSettings],
  templateUrl: './profile-menu.html',
  styleUrl: './profile-menu.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileMenuComponent implements OnInit {
  private static readonly LOCALIZATION_KEYS = [
    'app.layout.menu.ariaLabel',
    'app.layout.menu.profile',
    'app.layout.menu.companySettings',
    'app.layout.menu.logout',
    'app.layout.menu.language.nl',
    'app.layout.menu.language.en',
    'app.layout.menu.theme.default',
    'app.layout.menu.theme.light',
    'app.layout.menu.theme.dark',
    'app.layout.menu.theme.highContrast',
  ] as const;

  private readonly destroyRef = inject(DestroyRef);
  private readonly authStore = inject(AuthStore);
  private readonly authService = inject(AuthService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly localeService = inject(LocaleService);
  private readonly notification = inject(NotificationService);
  private readonly router = inject(Router);
  private readonly themeService = inject(ThemeService);
  private readonly translateService = inject(TranslateService);
  private readonly userApi = inject(UserApi);

  readonly closed = output<void>();

  protected readonly currentUser = this.currentUserService.currentUser;
  protected readonly firstName = computed(() => this.currentUser()?.appUser?.firstName?.trim() ?? '');
  protected readonly lastName = computed(() => this.currentUser()?.appUser?.lastName?.trim() ?? '');
  protected readonly companyName = computed(() => {
    const appUser = this.currentUser()?.appUser;
    return appUser?.companyName?.trim() ?? appUser?.company?.companyName?.trim() ?? appUser?.company?.name?.trim() ?? '';
  });
  protected readonly initials = computed(() =>
    `${this.firstName().charAt(0)}${this.lastName().charAt(0)}`.toUpperCase() || '-',
  );
  protected readonly canAccessSettings = computed(() =>
    this.authStore.hasAnyPermissionIn(['PERM_MODULE_', 'PERM_TENANT_', 'PERM_PLATFORM_']),
  );
  protected readonly selectedLanguage = signal<'NL' | 'EN'>('NL');
  protected readonly selectedColor = signal<UiColor>(0);
  protected readonly isSettingsModalOpen = signal(false);
  protected readonly languageOptions = signal<readonly MenuOption<'NL' | 'EN'>[]>([]);
  protected readonly colorOptions = signal<readonly MenuOption<UiColor>[]>([]);
  protected readonly labels = signal<Record<string, string>>({});

  ngOnInit(): void {
    this.currentUserService.getCurrentUser().then((user) => {
      const preference = user?.appUser?.uiColor;
      this.selectedColor.set(
        preference === null || preference === undefined
          ? this.themeService.preference()
          : this.themeService.applyPreference(preference),
      );
    }).catch(() => undefined);

    this.selectedLanguage.set(this.translateService.getCurrentLang()?.toUpperCase() === 'EN' ? 'EN' : 'NL');
    this.translateService
      .stream([...ProfileMenuComponent.LOCALIZATION_KEYS])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((translations) => {
        const values = translations as Record<string, string>;
        this.labels.set(values);
        this.languageOptions.set([
          { label: values['app.layout.menu.language.nl'] ?? 'NL', value: 'NL' },
          { label: values['app.layout.menu.language.en'] ?? 'EN', value: 'EN' },
        ]);
        this.colorOptions.set([
          { label: values['app.layout.menu.theme.default'] ?? 'Default', value: 0 },
          { label: values['app.layout.menu.theme.light'] ?? 'Light', value: 1 },
          { label: values['app.layout.menu.theme.dark'] ?? 'Dark', value: 2 },
          { label: values['app.layout.menu.theme.highContrast'] ?? 'High contrast', value: 3 },
        ]);
      });
  }

  protected label(key: string): string {
    return this.labels()[key] ?? key;
  }

  protected onLanguageChanged(value: unknown): void {
    if (value !== 'NL' && value !== 'EN') return;
    this.selectedLanguage.set(value);
    const locale = value.toLowerCase();
    this.localeService.setLocale(locale);
    this.translateService.use(locale).subscribe();
  }

  protected async onColorChanged(value: unknown): Promise<void> {
    const nextColor = this.themeService.applyPreference(value);
    this.selectedColor.set(nextColor);
    const userId = this.currentUser()?.id;
    if (!userId) return;

    try {
      await firstValueFrom(this.userApi.update(userId, { uiColor: nextColor }));
      await this.currentUserService.getCurrentUser(true);
    } catch {
      this.notification.info(this.translateService.instant('app.layout.menu.notifications.themeUpdateFailed'));
    }
  }

  protected openProfileSettings(): void {
    this.isSettingsModalOpen.set(true);
    this.closed.emit();
  }

  protected navigateToSettings(): void {
    const route = this.resolveSettingsRoute();
    void this.router.navigate([route]);
    this.closed.emit();
  }

  protected closeProfileSettings(): void {
    this.isSettingsModalOpen.set(false);
  }

  protected logout(): void {
    this.authService.logout();
    this.closed.emit();
  }

  private resolveSettingsRoute(): string {
    if (this.authStore.hasAnyPermissionIn(['PERM_MODULE_CHATBOT_'])) return '/settings/chatbots';
    if (this.authStore.hasAnyPermissionIn(['PERM_TENANT_COMPANY_'])) return '/settings/general';
    if (this.authStore.hasAnyPermissionIn(['PERM_TENANT_USER_', 'PERM_TENANT_ROLE_', 'PERM_TENANT_TEAM_'])) return '/settings/usermanagement';
    if (this.authStore.hasAnyPermissionIn(['PERM_TENANT_LICENSE_', 'PERM_TENANT_BILLING_'])) return '/settings/subscription';
    if (this.authStore.hasAnyPermissionIn(['PERM_TENANT_INTEGRATION_'])) return '/settings/integrations';
    if (this.authStore.hasAnyPermissionIn(['PERM_PLATFORM_TENANT_'])) return '/support/tenants';
    if (this.authStore.hasAnyPermissionIn(['PERM_PLATFORM_CONTRACT_', 'PERM_PLATFORM_ORDER_'])) return '/support/contracts';
    return '/settings/general';
  }
}
